import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import useSEO from '../hooks/useSEO';

const { FiArrowLeft, FiClock, FiUser, FiCalendar, FiArrowRight, FiZap, FiTrendingUp } = FiIcons;

const monthNumbers = {
  jan: '01',
  feb: '02',
  mar: '03',
  apr: '04',
  may: '05',
  jun: '06',
  jul: '07',
  aug: '08',
  sep: '09',
  oct: '10',
  nov: '11',
  dec: '12',
  ene: '01',
  abr: '04',
  ago: '08',
  oct: '10',
  dic: '12'
};

const toIsoDate = (date) => {
  const english = date.match(/^([A-Za-z]{3})\s+(\d{1,2}),?\s+(\d{4})$/);
  const spanish = date.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  const match = english || spanish;

  if (!match) return date;

  const month = (english ? match[1] : match[2]).toLowerCase();
  const day = (english ? match[2] : match[1]).padStart(2, '0');
  const year = english ? match[3] : match[3];
  const monthNumber = monthNumbers[month];

  return monthNumber ? `${year}-${monthNumber}-${day}` : date;
};

const InsightArticle = ({
  title,
  description,
  date,
  readTime,
  author = 'Mathias Deppe',
  category,
  keyTakeaway,
  children
}) => {
  const { lang } = useLanguage();
  const tidycalUrl = 'https://tidycal.com/mathiasdeppe/free-15-minute-growth-review';
  const publishedDate = toIsoDate(date);

  useSEO(`${title} | Mathias Deppe`, description);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description,
    author: {
      '@type': 'Person',
      name: author
    },
    datePublished: publishedDate,
    publisher: {
      '@type': 'Organization',
      name: 'Mathias Deppe',
      logo: {
        '@type': 'ImageObject',
        url: 'https://mdlens.com/favicon.svg'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://mdlens.com/insights/${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`
    }
  };

  return (
    <div className="bg-zinc-950 text-white min-h-screen pt-32 pb-24 selection:bg-orange-500/30">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <article className="max-w-6xl mx-auto px-6">
        <Link
          to="/insights"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-orange-500 font-bold uppercase tracking-widest text-xs mb-12 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} />
          {lang === 'es' ? 'Volver a Insights' : 'Back to Insights'}
        </Link>

        <header className="mb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-block px-3 py-1 bg-orange-500/10 border border-orange-500/20 text-orange-500 text-[10px] font-black tracking-widest uppercase rounded-full mb-8">
              {category}
            </div>
            <h1 className="text-4xl md:text-7xl font-black tracking-tighter uppercase leading-[0.9] mb-10">
              {title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-4 text-zinc-500 text-sm font-bold border-y border-white/5 py-6">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiUser} className="text-orange-500" />
                {author}
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCalendar} className="text-orange-500" />
                <time dateTime={publishedDate}>
                  {lang === 'es' ? 'Publicado el' : 'Published'} {date}
                </time>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiClock} className="text-orange-500" />
                {readTime} {lang === 'es' ? 'min de lectura' : 'min read'}
              </div>
            </div>
          </motion.div>
        </header>

        {keyTakeaway && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16 p-8 bg-zinc-900 border-l-4 border-orange-500 rounded-r-[2rem]"
          >
            <div className="flex items-center gap-2 text-orange-500 mb-4 font-black text-xs uppercase tracking-widest">
              <SafeIcon icon={FiZap} />
              {lang === 'es' ? 'PUNTO CLAVE' : 'KEY TAKEAWAY'}
            </div>
            <p className="text-xl md:text-2xl text-white font-bold leading-tight italic">
              "{keyTakeaway}"
            </p>
          </motion.div>
        )}

        <div className="max-w-3xl mx-auto">
          <div className="prose prose-invert prose-orange max-w-none prose-headings:uppercase prose-headings:tracking-tighter prose-headings:font-black prose-p:text-zinc-400 prose-p:leading-relaxed prose-p:text-lg">
            {children}
          </div>

          <motion.div
            whileInView={{ opacity: 1, scale: 1 }}
            initial={{ opacity: 0, scale: 0.95 }}
            viewport={{ once: true }}
            className="mt-24 p-10 md:p-16 bg-zinc-900 border-2 border-orange-500 rounded-[3rem] text-center relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-8 opacity-5">
              <SafeIcon icon={FiTrendingUp} className="text-8xl" />
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4 tracking-tighter uppercase leading-tight">
              {lang === 'es'
                ? '¿Quieres encontrar la fuga en tu proceso de ventas?'
                : 'What is the next step for your customer journey?'}
            </h2>
            <p className="text-zinc-400 text-lg mb-10 max-w-xl mx-auto">
              {lang === 'es'
                ? 'Reserva una Auditoría de Crecimiento gratuita de 15 minutos.'
                : 'Book a free 15-minute Growth Review.'}
            </p>
            <a
              href={tidycalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-10 py-5 bg-white text-zinc-950 font-black rounded-full hover:bg-orange-500 hover:text-white transition-all transform hover:scale-105 shadow-2xl group"
            >
              {lang === 'es' ? '→ Reservar Auditoría' : '→ Book Growth Review'}
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-2 transition-transform" />
            </a>
          </motion.div>
        </div>
      </article>
    </div>
  );
};

export default InsightArticle;